import { useT } from "../../lib/use-t";

export type SchrijvenTip = {
  /** Mono tag key resolved by the caller (already-translated label). */
  tag: string;
  tone: "good" | "tip" | "warn";
  /** Already-translated italic body. */
  body: string;
};

interface SchrijvenTutorLiveProps {
  tips: SchrijvenTip[];
}

const TONE_TAG_COLOR: Record<SchrijvenTip["tone"], string> = {
  good: "var(--pass)",
  tip: "var(--accent)",
  warn: "var(--warn)",
};

/**
 * STATIC writing-tips panel. There is NO live/streaming grader — the only
 * mid-typing call returns bullet-coverage booleans, no prose. So this panel
 * renders fixed, generic A2 coaching tips and is honestly labelled
 * "Schrijftips" (no "LIVE" claim). Substantive grammar feedback arrives on
 * submit via SchrijvenFeedbackBubble. Content is static → no live region.
 */
export function SchrijvenTutorLive({ tips }: SchrijvenTutorLiveProps) {
  const t = useT();
  if (tips.length === 0) return null;

  return (
    <section
      aria-labelledby="schrijftips-title"
      className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--accent-soft)] p-4"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <span
          className="grid place-items-center w-[30px] h-[30px] rounded-full bg-[var(--accent)] text-white font-mono-badge text-xs shrink-0"
          aria-hidden
        >
          T
        </span>
        <span id="schrijftips-title" className="text-[13.5px] font-semibold text-[var(--ink)]">
          {t("schrijven.runner.tutorTipsTitle")}
        </span>
      </div>

      <div className="space-y-2.5">
        {tips.map((tip, i) => (
          <article
            key={i}
            className="rounded-[14px] border border-[var(--border)] bg-[var(--panel)] px-4 py-3"
          >
            <span
              className="block font-mono-badge text-[9.5px] uppercase tracking-wider mb-1.5"
              style={{ color: TONE_TAG_COLOR[tip.tone] }}
            >
              {tip.tag}
            </span>
            <p className="font-serif-italic text-[14.5px] text-[var(--ink)] leading-snug">
              {tip.body}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

/**
 * The static A2 writing tips shown in the runner. Hook so callers get the
 * translated strings via i18n. Kept generic + honest — not per-keystroke AI.
 */
export function useSchrijvenTips(): SchrijvenTip[] {
  const t = useT();
  return [
    { tag: t("schrijven.runner.tagUForm"), tone: "good", body: t("schrijven.runner.tipUForm") },
    { tag: t("schrijven.runner.tagAllPoints"), tone: "tip", body: t("schrijven.runner.tipAllPoints") },
    { tag: t("schrijven.runner.tagClosing"), tone: "tip", body: t("schrijven.runner.tipClosing") },
  ];
}
